import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CATALOGUE_PROBLEM_CODES,
  CATALOGUE_PROBLEM_SEVERITY,
  type CatalogueHealthPage,
  type CatalogueHealthRow,
  type CatalogueProblemCode,
} from "@shared/catalogue-health";
import { foyerApi } from "../../http/foyer";
import { tokens } from "../../theme";
import {
  catalogueProblemLabel,
  catalogueProblemText,
  saveErrorText,
} from "./findings";

/**
 * Content health, on the Overview page: every title that cannot be played and
 * every channel missing metadata, one click from the editor that fixes it.
 *
 * The verdicts come from `GET /distribution/health`, which sweeps the whole
 * catalogue and holds the answer for a few minutes - so the caption always says
 * WHEN it was checked. A list that looks live and is not is worse than one that
 * admits its age.
 */

const PAGE_SIZE = 10;

const TYPE_LABELS: Record<string, string> = {
  channel: "Channel",
  movie: "Movie",
  episode: "Episode",
};

/** Where a row is fixed. The list pages open `?open=<assetId>` in their editor. */
export function healthRowHref(row: CatalogueHealthRow): string {
  const section = row.subject === "channel" ? "channels" : "vod";
  return `/distribution/${section}?open=${encodeURIComponent(row.assetId)}`;
}

function checkedAgo(iso: string, now: number): string {
  const minutes = Math.floor((now - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(minutes) || minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ago`;
}

function formatCount(n: number): string {
  return n.toLocaleString("sv-SE");
}

function episodeLabel(row: CatalogueHealthRow): string | null {
  if (row.type !== "episode") return null;
  const parts = [
    row.seasonNumber != null ? `S${row.seasonNumber}` : null,
    row.episodeNumber != null ? `E${row.episodeNumber}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * Is this actually a health report?
 *
 * The panel lives on the landing page, so a body of the wrong shape must fail
 * as the PANEL - never as a render crash that blanks the whole Overview. That
 * is not hypothetical: a stubbed or misrouted `fetch` answering `[]` took the
 * entire app down before this guard existed.
 */
function isHealthPage(value: unknown): value is CatalogueHealthPage {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const page = value as Partial<CatalogueHealthPage>;
  return (
    !!page.counts &&
    typeof page.counts === "object" &&
    Array.isArray(page.rows) &&
    !!page.pagination &&
    !!page.scanned
  );
}

export default function CatalogueHealthPanel() {
  const navigate = useNavigate();
  const [problem, setProblem] = useState<CatalogueProblemCode | null>(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CatalogueHealthPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A chip clicked twice quickly must not let the slower, older answer land
  // last and relabel the list under the newer filter.
  const latest = useRef(0);

  const load = useCallback(
    async (refresh: boolean) => {
      const request = ++latest.current;
      setLoading(true);
      setError(null);
      try {
        const answer = await foyerApi.catalogueHealth({
          ...(problem ? { problem } : {}),
          page,
          pageSize: PAGE_SIZE,
          ...(refresh ? { refresh: true } : {}),
        });
        if (request !== latest.current) return;
        if (!isHealthPage(answer)) {
          setData(null);
          setError("the server's answer was not a health report.");
          return;
        }
        setData(answer);
      } catch (e) {
        if (request !== latest.current) return;
        const err = e as { code?: string; message: string };
        setError(saveErrorText(err.code, err.message));
      } finally {
        if (request === latest.current) setLoading(false);
      }
    },
    [problem, page]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  function pick(code: CatalogueProblemCode | null) {
    setProblem((current) => (current === code ? null : code));
    setPage(1);
  }

  const present = data
    ? CATALOGUE_PROBLEM_CODES.filter((code) => data.counts[code] > 0)
    : [];
  const allClear = data !== null && present.length === 0;

  return (
    <Card data-testid="catalogue-health">
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Stack spacing={0.25}>
            <Typography variant="h6">Content health</Typography>
            <Typography
              sx={{ fontSize: 13, color: tokens.inkMuted }}
              data-testid="catalogue-health-caption"
            >
              {data
                ? `Checked ${checkedAgo(data.checkedAt, Date.now())} · ${formatCount(
                    data.scanned.titles
                  )} titles and ${formatCount(data.scanned.channels)} channels read`
                : "Reading the whole catalogue - the first check takes a moment."}
            </Typography>
          </Stack>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => void load(true)}
            disabled={loading}
            data-testid="catalogue-health-refresh"
          >
            Check again
          </Button>
        </Stack>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-testid="catalogue-health-error"
          >
            Could not check the catalogue: {error}
          </Alert>
        )}

        {data?.truncated && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The catalogue is larger than one check reads. The numbers below are
            a floor, not a total.
          </Alert>
        )}

        {allClear && !error && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid="catalogue-health-clear"
          >
            <CheckCircleOutlineIcon sx={{ color: tokens.success }} />
            <Typography variant="body2">
              Every title can be played and every channel has its metadata.
            </Typography>
          </Stack>
        )}

        {present.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Chip
              size="small"
              label="All problems"
              color={problem === null ? "primary" : "default"}
              variant={problem === null ? "filled" : "outlined"}
              onClick={() => pick(null)}
              data-testid="catalogue-health-filter-all"
            />
            {present.map((code) => {
              const selected = problem === code;
              const color =
                CATALOGUE_PROBLEM_SEVERITY[code] === "error"
                  ? "error"
                  : "warning";
              return (
                <Chip
                  key={code}
                  size="small"
                  label={`${catalogueProblemLabel(code)} (${formatCount(
                    data!.counts[code]
                  )})`}
                  color={color}
                  variant={selected ? "filled" : "outlined"}
                  onClick={() => pick(code)}
                  data-testid={`catalogue-health-filter-${code}`}
                />
              );
            })}
          </Stack>
        )}

        {!data && loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={36} />
            ))}
          </Stack>
        )}

        {data && data.rows.length > 0 && (
          <>
            <TableContainer sx={{ opacity: loading ? 0.6 : 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell sx={{ width: 110 }}>Kind</TableCell>
                    <TableCell>Problem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.map((row) => {
                    // The filtered problem leads, so the sentence on the row is
                    // the one the chip promised.
                    const problems = problem
                      ? [...row.problems].sort(
                          (a, b) =>
                            Number(b.code === problem) -
                            Number(a.code === problem)
                        )
                      : row.problems;
                    const [first, ...more] = problems;
                    const episode = episodeLabel(row);
                    const open = () => navigate(healthRowHref(row));
                    return (
                      <TableRow
                        key={row.assetId}
                        hover
                        tabIndex={0}
                        sx={{ cursor: "pointer" }}
                        data-testid={`catalogue-health-row-${row.assetId}`}
                        onClick={open}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();
                          open();
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {row.title || "(no name)"}
                          </Typography>
                          {episode && (
                            <Typography
                              sx={{ fontSize: 12, color: tokens.inkMuted }}
                            >
                              {episode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={TYPE_LABELS[row.type] ?? row.type}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13 }}>
                            {catalogueProblemText(first)}
                          </Typography>
                          {more.length > 0 && (
                            <Tooltip
                              title={
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                  {more.map((p) => (
                                    <li key={p.code}>
                                      {catalogueProblemText(p)}
                                    </li>
                                  ))}
                                </Box>
                              }
                            >
                              <Typography
                                component="span"
                                sx={{ fontSize: 12, color: tokens.inkMuted }}
                              >
                                +{more.length} more
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.pagination.total}
              page={page - 1}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[]}
              onPageChange={(_e, next) => setPage(next + 1)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
